import type { PianoParticleEmission } from './pianoRenderTypes';

/**
 * Append an alpha (`0..1`) to a `#RRGGBB` palette colour. Doesn't
 * support shorthand `#RGB` values — the project's palette validator
 * already enforces 6-digit hex (see {@link import('@shared/chart').HEX_COLOUR_RE}),
 * so the renderer doesn't need to handle 3-digit input.
 */
export function withAlpha(hex: string, alpha: number): string {
  const a = Math.max(0, Math.min(1, alpha));
  if (hex.length === 7 && hex.startsWith('#')) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  return hex;
}

/**
 * Lighten a `#RRGGBB` colour by mixing it toward white at `amount`
 * (0..1). Used to give hold tiles a subtly brighter, warmer tint than
 * tap tiles so the two read as distinct objects across every chart
 * palette (we can't just shift toward a fixed accent because palettes
 * already span the full hue wheel — toxic green to inferno amber).
 */
export function lightenToward(hex: string, amount: number): string {
  if (hex.length !== 7 || !hex.startsWith('#')) {
    return hex;
  }
  const a = Math.max(0, Math.min(1, amount));
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mixR = Math.round(r + (255 - r) * a);
  const mixG = Math.round(g + (255 - g) * a);
  const mixB = Math.round(b + (255 - b) * a);
  const toHex = (n: number): string => n.toString(16).padStart(2, '0');
  return `#${toHex(mixR)}${toHex(mixG)}${toHex(mixB)}`;
}

/**
 * Map a combo count to a tier index 0..3 used to look up the
 * background bloom + hit-line glow colour. Thresholds chosen to
 * match COMBO_BG_TIER_THRESHOLD and the combo-milestone banners
 * (10 / 25 / 50 / 100).
 */
export function comboTier(combo: number): 0 | 1 | 2 | 3 {
  if (combo >= 100) {
    return 3;
  }
  if (combo >= 50) {
    return 2;
  }
  if (combo >= 25) {
    return 1;
  }
  return 0;
}

/** Default particle colour per emission type when the session omits an override. */
export function colourForEmission(type: PianoParticleEmission['type']): string {
  switch (type) {
    case 'hit_perfect':
      return '#3bff9d';
    case 'hit_good':
      return '#16e0ff';
    case 'hit_miss':
      return '#ff3b5c';
    case 'milestone':
      return '#ffd84d';
    case 'death':
      return '#ff3b5c';
    case 'hold_sustain':
      return '#16e0ff';
    case 'hold_release':
      return '#3bff9d';
  }
}
